// src/pages/LaptopViewer.js
import React, { useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import LaptopModel from "../components/LaptopModel";
import "./LaptopViewer.css";
import { FaWhatsapp } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form } from "react-bootstrap";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import logo from '../assets/TT logo.png';
import { Link } from "react-router-dom";

const partNameAliases = {
  Object_4: "Keyboard",
  Object_15: "Secondary Display Screen",
  Object_22: "Main Display Screen",
  Object_42: "Touchpad",
  Object_33: "Overheating & Fan",
  Object_72: "Overheating & Fan",
  Object_57: "Overheating & Fan",
  Object_38: "Battery",
  Object_29: "Camera & Webcam",
  Object_67: "Ports & Charging",
  Object_68: "Ports & Charging",
  Object_35: "Ports & Charging",
  Object_63: "Audio",
};

const partIssues = {
  Keyboard: [
    "Some keys not working",
    "Entire keyboard dead",
    "Backlight not turning on",
    "Keyboard damage",
    "Double typing or stuck keys",
  ],
  "Main Display Screen": [
    "Screen flickers",
    "LCD broken",
    "No display on boot",
    "Black screen randomly",
  ],
  "Secondary Display Screen": [
    "Screen flickers",
    "LCD broken",
    "No display on boot",
  ],
  "Overheating & Fan": ["Laptop gets too hot", "Fans running loud constantly"],
  Touchpad: ["Trackpad Unresponsive", "Laggy behavior"],
  "Ports & Charging": [
    "Laptop not charging",
    "Charger not recognized",
    "Loose charging port",
  ],
  Battery: ["Battery drains fast", "Battery not charging fully"],
  "Camera & Webcam": ["Webcam not turning on", "Black screen on webcam"],
  Audio: ["No sound from speakers", "Distorted or crackling audio"],
  Hinges: ["Stiff hinge", "Broken hinge"],
  Motherboard: [
    "GPU not detected",
    "Games crash on launch",
    "Artifacts on screen",
    "Stuttering or lag in games",
    "Black screen during gameplay",
    "External GPU not working",
    "Display runs on Intel GPU only",
    "Driver crash or error",
    "Laptop not powering on",
    "No POST or beeps",
    "Liquid spill on board",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Faulty IC",
    "USB ports not working",
    "Thunderbolt port failure",
    "SSD/HDD not detected",
    "OS not booting",
    "Stuck on logo screen",
    "Boot device not found",
    "Frequent BSODs",
    "Slow boot time",
    "SSD overheating",
    "Files corrupted randomly",
  ],
};

const LaptopViewer = () => {
  const [selectedPart, setSelectedPart] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(true); // Add this line at the top

  const getAlias = (rawName) => partNameAliases[rawName] || rawName;
  const hoveredAlias = getAlias(hoveredPart);
  const initialCameraPosition = [0, 0.5, 0.5];

  const handlePartClick = (partOrAlias) => {
    const alias = getAlias(partOrAlias);
    setSelectedPart(alias);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedPart || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the laptop with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the laptop model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);

  return (
    <>
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
        <Link to={'/'}><img src={logo} style={{ height: '160px', margin: '-40px -30px', padding: '0px' }} /></Link>
        <Button variant="info" onClick={startTour}>
          📖 Show Instructions
        </Button>
      </header>
      <div className="laptop-viewer-container">
        {/* 3D Model */}
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <LaptopModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart}
              partNameAliases={partNameAliases}
              onLoaded={() => setLoading(false)} // ✅ Correct prop name
            />


            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        {/* Sidebar */}
        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>🛠️ All Parts</h3>
              <ul>
                {Array.from(new Set(Object.values(partNameAliases))).map(
                  (alias, i) => (
                    <li
                      key={i}
                      onClick={() => handlePartClick(alias)}
                      style={{ cursor: "pointer" }}
                    >
                      {alias}
                    </li>
                  )
                )}
                <li
                  onClick={() => handlePartClick("Motherboard")}
                  style={{ cursor: "pointer" }}
                >
                  Motherboard
                </li>
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedPart} Issues</h3>
              <ul>
                {(partIssues[selectedPart] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: "pointer" }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LaptopViewer;
